// ============================================================
// TMR Portal Admin — Meu perfil (dados pessoais + alterar senha)
// Observação: nome/telefone/senha aqui são UI; a persistência real
// passará pelo Clerk/backend (não implementada nesta fase).
// ============================================================
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { TmrSession } from '@tmr/auth';
import { Avatar, Btn } from '../../components/primitives';
import { PageHeader } from '../../components/ui';
import { TextField } from '../../components/fields';
import { PhoneField } from '../../components/PhoneField';
import { Icons, StrokeIcon } from '../../lib/icons';

export interface ProfilePageProps {
  /** The operator behind the session cookie — present whichever way they signed in (F1). */
  operator: TmrSession;
  onSignOut: () => void | Promise<void>;
}

export function ProfilePage({ operator, onSignOut }: ProfilePageProps) {
  const displayName = operator.name || operator.email || 'Usuário';
  const email = operator.email;

  const timers = useRef<number[]>([]);
  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  const [nome, setNome] = useState(displayName);
  const [telefone, setTelefone] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [touched, setTouched] = useState(false);
  const [savedProfile, setSavedProfile] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const nomeErr = nome.trim().length < 3 ? 'Informe o nome completo' : null;
  const saveProfile = () => {
    if (nomeErr) {
      setTouched(true);
      return;
    }
    setSavedProfile(true);
    later(() => setSavedProfile(false), 2200);
  };

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPhoto(URL.createObjectURL(file));
  };

  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [touchedPwd, setTouchedPwd] = useState(false);
  const [savedPwd, setSavedPwd] = useState(false);
  const pwdErrs = {
    atual: !atual ? 'Informe a senha atual' : null,
    nova: nova.length < 8 ? 'Use pelo menos 8 caracteres' : null,
    confirma: confirma !== nova ? 'As senhas não coincidem' : null,
  };
  const savePwd = () => {
    if (pwdErrs.atual || pwdErrs.nova || pwdErrs.confirma) {
      setTouchedPwd(true);
      return;
    }
    setSavedPwd(true);
    setAtual('');
    setNova('');
    setConfirma('');
    setTouchedPwd(false);
    later(() => setSavedPwd(false), 2200);
  };

  const pwdField = (label: string, value: string, setValue: (v: string) => void, error: string | null) => (
    <div>
      <label className="pa-form-label">
        {label} <span style={{ color: 'var(--danger)' }}>*</span>
      </label>
      <input
        type={showPwd ? 'text' : 'password'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="pa-input"
        style={{ width: '100%', padding: '9px 12px', fontFamily: 'var(--font-sans)', fontSize: 14, border: `1px solid ${error ? 'var(--danger)' : 'var(--border-default)'}`, borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)', color: 'var(--ink-900)', outline: 'none' }}
      />
      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{error}</div>}
    </div>
  );

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>
      <PageHeader
        eyebrow="Conta"
        title="Meu perfil"
        desc="Suas informações pessoais e credenciais de acesso."
        actions={
          <Btn variant="secondary" icon={Icons.logout} onClick={() => void onSignOut()}>
            Sair da conta
          </Btn>
        }
      />

      <div className="pa-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="pa-eyebrow">Dados pessoais</div>
        <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {photo ? (
              <img src={photo} alt="Foto de perfil" style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <Avatar name={nome || displayName} size={84} />
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />
            <button type="button" onClick={() => fileRef.current?.click()} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--forest-700)', fontSize: 12, fontWeight: 500 }}>
              Alterar foto
            </button>
          </div>
          <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="pa-grid-2">
              <TextField label="Nome completo" required value={nome} onChange={setNome} error={touched && nomeErr} />
              <div>
                <label className="pa-form-label">E-mail de login</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-100)', color: 'var(--fg-muted)', fontSize: 14 }}>
                  <StrokeIcon d={Icons.shield} size={14} style={{ flexShrink: 0, color: 'var(--stone-400)' }} />
                  {email || '—'}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 5 }}>Não pode ser alterado por aqui.</div>
              </div>
            </div>
            <div className="pa-grid-2">
              <PhoneField value={telefone} onChange={setTelefone} />
              <TextField label="Data de nascimento" type="date" value={nascimento} onChange={setNascimento} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Btn variant="primary" icon={savedProfile ? Icons.check : undefined} onClick={saveProfile}>
            {savedProfile ? 'Salvo' : 'Salvar alterações'}
          </Btn>
        </div>
      </div>

      <div className="pa-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="pa-eyebrow">Segurança · alterar senha</div>
        <div className="pa-grid-3">
          {pwdField('Senha atual', atual, setAtual, touchedPwd ? pwdErrs.atual : null)}
          {pwdField('Nova senha', nova, setNova, touchedPwd ? pwdErrs.nova : null)}
          {pwdField('Confirmar nova senha', confirma, setConfirma, touchedPwd ? pwdErrs.confirma : null)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <button type="button" onClick={() => setShowPwd((s) => !s)} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--forest-700)', fontSize: 12.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5, padding: 0 }}>
            <StrokeIcon d={showPwd ? Icons.eyeOff : Icons.eye} size={14} /> {showPwd ? 'Ocultar senhas' : 'Mostrar senhas'}
          </button>
          <Btn variant="primary" icon={savedPwd ? Icons.check : undefined} onClick={savePwd}>
            {savedPwd ? 'Senha alterada' : 'Salvar nova senha'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
